import {Component, Inject, OnDestroy, OnInit, Optional} from '@angular/core';
import {
  CommissionFeePeriodPriceWithLevel,
  CommissionLevel,
  Fee,
  FeePeriodPrice,
  FeePeriodPricePost,
  FeePost,
  FeePut,
  FeeType,
  Period,
  SalespointWithAlias,
  TranslationValue
} from '../../../generated/sales-terminal-client';
import {LanguageService} from '../../../services/language/language.service';
import {AuthService} from '../../../services/auth/auth.service';
import {cloneDeep, find} from 'lodash-es';
import {forkJoin, Observable, ReplaySubject, Subscription, tap} from 'rxjs';
import {SalespointsService} from '../../../services/salespoints/salespoints.service';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from '@angular/material/dialog';
import {PendingChangesDialogComponent} from '../../pending-changes-dialog/pending-changes-dialog.component';
import {CONTINUE_WITHOUT_SAVING} from '../../../modules/sales-terminal/sales-terminal.constants';
import {TranslateService} from '@ngx-translate/core';
import {FeeService} from '../../../services/fee/fee.service';
import {finalize} from 'rxjs/operators';
import {SnackService} from '../../../services/snack/snack.service';
import {SortedFee} from '../service-fees/service-fees.component';
import {UtilService} from '../../../services/util/util.service';
import {NumericInputType} from '../../forms/numeric-input/numeric-input.component';
import {DatePickerConfig} from '../../datepicker/datepicker.component';
import moment from 'moment-timezone';
import {ValidationService} from '../../../services/validation/validation.service';

export const SERVICE_FEE_NAME_MAX_LENGTH: number = 50;
export const SERVICE_FEE_TRANSLATION_MAX_LENGTH: number = 34;
export interface ActivePeriodPrice extends FeePeriodPrice {
  uniqueId?: string;
}

@Component({
  selector: 'app-service-fee-dialog',
  templateUrl: './service-fee-dialog.component.html',
  styleUrls: ['./service-fee-dialog.component.scss']
})
export class ServiceFeeDialogComponent implements OnInit, OnDestroy {
  public isLoading: boolean;
  public serviceFee: SortedFee;
  public activeTab: number = 0;
  public activePeriodPrice: ActivePeriodPrice;
  public sortedLanguages: string[] = [];
  public translations: {[key: string]: string;} = {};
  public salespoints: SalespointWithAlias[] = [];
  public salespointsFiltered: ReplaySubject<SalespointWithAlias[]> = new ReplaySubject<SalespointWithAlias[]>(1);
  public isPeriodCreate: boolean;
  public showEndDatepicker: boolean;
  public isPeriodStartValid: boolean = true;
  public isPeriodEndValid: boolean = true;
  public datePickerConfig: DatePickerConfig = {
    errorMessage: 'general.api_fees_time_invalid',
    timepicker: true, autoBindDates: true, allowDateClear: true, noHint: true, noPresets: true,
    minDate: moment.utc().toISOString()
  };
  private _subs: Subscription[] = [];
  private _originalTranslations: TranslationValue[] = [];
  private _origRefundableValue: boolean;
  private _origSalespoints: number[] = [];
  public minEndDate: string;
  public erroringSalespoints: number[] = [];
  public legalVatPercentage: number;
  public areTranslationsValid: boolean;
  public periodPricesForPost: Record<string, FeePeriodPricePost> = {};

  constructor(public utilService: UtilService,
              public dialogRef: MatDialogRef<ServiceFeeDialogComponent>,
              @Optional() @Inject(MAT_DIALOG_DATA) public data: SortedFee,
              public authService: AuthService,
              private dialog: MatDialog,
              private languageService: LanguageService,
              private salespointsService: SalespointsService,
              private translateService: TranslateService,
              private feeService: FeeService,
              private snackService: SnackService) {
    this.serviceFee = data || {
      salespoints: [],
      periodPrices: [],
      type: FeeType.Service,
      translations: [],
      futurePeriodPrices: []
    };
  }

  public ngOnInit(): void {
    this.isLoading = true;
    this.legalVatPercentage = this.authService.salespoint.legalPerson?.vatPercent;
    const spRequest: Observable<SalespointWithAlias[]> = this.salespointsService.getSalesPoints({ includeAliasSalespoints: true });
    const langRequest: Observable<string[]> = this.languageService.getLanguages();
    forkJoin(spRequest, langRequest).pipe(
      finalize(() => this.isLoading = false)
    ).subscribe((results: [SalespointWithAlias[], string[]]) => {
      this.salespoints = results[0];
      this.sortedLanguages = this.languageService.sortLanguages(results[1], this.authService.salesPointLanguage);
      this.serviceFee.translations.forEach((translation: TranslationValue) => {
        this.translations[translation.language] = translation.value;
      });
      this._originalTranslations = cloneDeep(this.serviceFee?.translations);
      this._origSalespoints = this.serviceFee?.salespoints || [];
      this.validateTranslations();
    });
    this._origRefundableValue = this.serviceFee.isRefundable;
    this.dialogRef.disableClose = true;
    this._subs.push(this.dialogRef.backdropClick().subscribe(() => {
      this.confirmClose();
    }));
  }
  public ngOnDestroy(): void {
    this._subs.forEach(s => s.unsubscribe());
    this._subs = [];
  }

  public saveFee(): void {
    this.isLoading = true;
    this.erroringSalespoints = [];
    if (!this.serviceFee.id) {
      this.feeService.addServiceFee(this.getServiceFeePost()).pipe(
        finalize(() => this.isLoading = false),
        tap({ error: (error) => this.updateErroringSalespoints(error?.error)})
      ).subscribe(_ => {
        this.snackService.showSuccess(this.translateService.instant('general.api_service_fees_create_success'));
        this.dialogRef.close();
      });
    } else {
      this.feeService.updateServiceFee(this.serviceFee.id, this.getServiceFeePut()).pipe(
        finalize(() => this.isLoading = false),
        tap({ error: (error) => this.updateErroringSalespoints(error?.error)})
      ).subscribe(_ => {
        this.snackService.showSuccess(this.translateService.instant('general.api_service_fees_save_success'));
        this.dialogRef.close();
      });
    }
  }
  private updateErroringSalespoints(error: any): void {
    if (error?.returnValue?.length) {
      this.erroringSalespoints = error.returnValue;
    }
  }
  private getServiceFeePost(): FeePost {
    const feePost: any = cloneDeep(this.serviceFee);
    delete feePost.id;
    this.deletePeriodPriceHelpers(feePost);
    feePost.periodPrices = Object.values(this.periodPricesForPost);
    feePost.translations = this.prepareTranslations();
    return feePost as FeePost;
  }
  private getServiceFeePut(): FeePut {
    const feePut: any = cloneDeep(this.serviceFee);
    delete feePut.periodPrices;
    this.deletePeriodPriceHelpers(feePut);
    feePut.translations = this.prepareTranslations();
    return feePut as FeePut;
  }
  private deletePeriodPriceHelpers(fee: SortedFee): SortedFee {
    delete fee.pastPeriodPrices;
    delete fee.currentPeriodPrice;
    delete fee.futurePeriodPrices;
    delete fee.currentAndFuturePeriodPrices;

    return fee;
  }
  private prepareTranslations(): TranslationValue[] {
    const translationValues: TranslationValue[] = [];
    Object.keys(this.translations).forEach(lang => {
      if (this.translations[lang].length) {
        translationValues.push({language: lang, value: this.translations[lang]});
      }
    });
    return translationValues;
  }

  public nameByIdDisplay(inId: number, inList: any[]): string {
    return find(inList, {id: inId})?.name;
  }

  public get isValid(): boolean {
    return !!this.serviceFee?.name?.length && this.serviceFee?.name?.length <= SERVICE_FEE_NAME_MAX_LENGTH &&
      !!this.serviceFee?.salespoints?.length && this.areTranslationsValid && !!this.translations['en']?.length;
  }

  public validateTranslations(): void {
    const translationValues: TranslationValue[] = this.prepareTranslations();
    this.areTranslationsValid = !!translationValues.length && !translationValues
      .some(translation => translation.value.length > SERVICE_FEE_TRANSLATION_MAX_LENGTH);
  }

  public get displaySelectedSalespoints(): string {
    let resultString: string = '';
    this.salespoints.forEach(salespoint => {
      if (this.serviceFee.salespoints.includes(salespoint.id)) {
        resultString += salespoint.name + '; ';
      }
    });
    return resultString.length ? resultString.trim().replace(/;+$/, '') : '-';
  }

  public addPeriodPrice(): void {
    this.activePeriodPrice = {id: null, feeAmount: null, vatPercentage: this.legalVatPercentage, period: {start: null, end: null}};
    this.activePeriodPrice.uniqueId = Date.now().toString();
    this.isPeriodCreate = true;
  }
  public editPeriodPrice(feePeriodPrice: FeePeriodPrice, uniqueId?: string): void {
    this.showEndDatepicker = !!feePeriodPrice.period?.end;
    this.activePeriodPrice = {
      id: feePeriodPrice.id,
      uniqueId: uniqueId,
      feeAmount: feePeriodPrice.feeAmount ? (feePeriodPrice.feeAmount/100) : null,
      vatPercentage: feePeriodPrice.vatPercentage,
      period: cloneDeep(feePeriodPrice.period),
    };
  }
  public removePeriodPrice(periodPriceId: number, uniqueId?: string): void {
    if (!periodPriceId && uniqueId) {
      delete this.periodPricesForPost[uniqueId];
    } else {
      this.isLoading = true;
      this.feeService.removeServiceFeePeriod(this.serviceFee.id, periodPriceId).pipe(
        finalize(() => this.finalizeRequest())
      ).subscribe((serviceFee: Fee) => this.finishPeriodSave(serviceFee, true));
    }
  }
  public savePeriodPrice(uniqueId?: string): void {
    this.isLoading = true;
    this.activePeriodPrice.feeAmount = this.activePeriodPrice.feeAmount != null ? (Math.round(this.activePeriodPrice.feeAmount*100)) : null;
    this.activePeriodPrice.vatPercentage = this.activePeriodPrice.vatPercentage != null ? +this.activePeriodPrice.vatPercentage : this.legalVatPercentage;
    const periodPricePost: FeePeriodPricePost = {
      feeAmount: this.activePeriodPrice.feeAmount,
      vatPercentage: this.activePeriodPrice.vatPercentage,
      period: this.activePeriodPrice.period
    };
    if (this.serviceFee.id) {
      if (this.activePeriodPrice.id) {
        this.feeService.updateServiceFeePeriod(this.serviceFee.id, this.activePeriodPrice).pipe(
          finalize(() => this.finalizeRequest())
        ).subscribe((serviceFee: Fee) => this.finishPeriodSave(serviceFee));
      } else {
        this.feeService.addServiceFeePeriod(this.serviceFee.id, periodPricePost).pipe(
          finalize(() => this.finalizeRequest())
        ).subscribe((serviceFee: Fee) => this.finishPeriodSave(serviceFee));
      }
    } else {
      this.periodPricesForPost[uniqueId] = periodPricePost;
      this.finalizeRequest();
    }
  }
  private finalizeRequest(): void {
    this.isLoading = false;
    this.clearActivePeriodPrice();
  }
  private finishPeriodSave(serviceFee: Fee, removal?: boolean): void {
    if (removal) {
      this.snackService.showSuccess(this.translateService.instant('general.api_fees_period_remove_success'));
    } else {
      this.snackService.showSuccess(this.translateService.instant('general.api_fees_period_save_success'));
    }
    this.serviceFee = this.feeService.sortServiceFeePeriods(serviceFee);
  }

  public clearActivePeriodPrice(): void {
    this.activePeriodPrice = null;
    this.showEndDatepicker = false;
    this.isPeriodStartValid = true;
    this.isPeriodEndValid = true;
    this.isPeriodCreate = false;
  }

  public isActivePeriodPrice(id: number, uniqueId?: string): boolean {
    return (!!id && this.activePeriodPrice?.id === id) || (!!uniqueId && this.activePeriodPrice?.uniqueId === uniqueId);
  }
  public isCurrentPeriodPrice(id: number, start: string): boolean {
    const currentUniqueId: string = this.serviceFee.currentPeriodPrice?.id + this.serviceFee.currentPeriodPrice?.period?.start;
    const periodUniqueId: string = id + start;
    return currentUniqueId === periodUniqueId;
  }

  public validatePeriodStart(): void {
    const start: moment.Moment = moment(this.activePeriodPrice.period?.start || null);
    const now: moment.Moment = moment(new Date());
    this.isPeriodStartValid = !this.activePeriodPrice.period?.start ||
      (start.isValid() && start.isAfter(now) && !this.isDuplicatePeriodStart(start, this.activePeriodPrice.id));
    if (this.isPeriodStartValid) {
      this.isPeriodStartValid = !this.isPeriodOverlapping();
    }
    this.validatePeriodEnd();
  }
  public validatePeriodEnd(): void {
    const now: moment.Moment = moment(new Date());
    const start: moment.Moment = moment(this.activePeriodPrice.period?.start || null);
    const end: moment.Moment = moment(this.activePeriodPrice.period?.end || null);
    this.isPeriodEndValid = !this.activePeriodPrice.period?.end || (end.isValid() && end.isAfter(start) && end.isSameOrAfter(now) && !this.isPeriodOverlapping());
    this.updateMinEndDate();
  }
  private isDuplicatePeriodStart(start: moment.Moment, id: number): boolean {
    return this.serviceFee.periodPrices?.length > 0 && this.serviceFee.periodPrices.filter((periodPrice: CommissionFeePeriodPriceWithLevel) =>
      periodPrice.id !== id && moment(periodPrice.period?.start || null).isSame(start)).length > 0;
  }
  private isPeriodOverlapping(): boolean {
    if (this.periodPricesForPostList?.length) {
      return Object.values(this.periodPricesForPost).some(periodPrice => this.checkOverlap(periodPrice.period));
    } else {
      return this.serviceFee.periodPrices.some(periodPrice => this.activePeriodPrice.id !== periodPrice.id && this.checkOverlap(periodPrice.period));
    }
  }
  private checkOverlap(periodPricePeriod: Period): boolean {
    const activeStart: moment.Moment = moment(this.activePeriodPrice.period?.start || null);
    const activeEnd: moment.Moment = moment(this.activePeriodPrice.period?.end || null);
    const start: moment.Moment = moment(periodPricePeriod?.start || null);
    const end: moment.Moment = moment(periodPricePeriod?.end || null);
    return (activeStart.isAfter(start) && activeStart.isBefore(end)) || (activeEnd.isValid() && activeEnd.isAfter(start) && activeEnd.isBefore(end));
  }

  public setDefaultEndDate(): void {
    this.updateMinEndDate();
    this.activePeriodPrice.period.end = moment(this.minEndDate).set('h', 23).set('m', 59).set('s', 0).toISOString();
    this.showEndDatepicker = true;
  }

  public updateMinEndDate(): void {
    const now: moment.Moment = moment(new Date());
    const start: moment.Moment = moment(this.activePeriodPrice.period?.start || null);
    this.minEndDate = start.isBefore(now) ? now.toISOString() : start.toISOString();
  }

  public toggleAllSalespoints(toggleAll: boolean): void {
    if (toggleAll) {
      this.serviceFee.salespoints = this.salespoints.map(sp => sp.id);
    } else {
      this.serviceFee.salespoints = [];
    }
  }

  public get canSavePeriodPrice(): boolean {
    return !!this.activePeriodPrice.feeAmount && !!this.activePeriodPrice.period?.start && this.isPeriodStartValid && this.isPeriodEndValid &&
      ValidationService.validatePercentage(this.activePeriodPrice.vatPercentage, true);
  }

  public get isModelChanged(): boolean {
    return !this.serviceFee?.id || !!this.activePeriodPrice || JSON.stringify(this._originalTranslations) !== JSON.stringify(this.serviceFee?.translations) ||
      this.serviceFee.isRefundable !== this._origRefundableValue || JSON.stringify(this.serviceFee?.salespoints) !== JSON.stringify(this._origSalespoints);
  }
  public confirmClose(): void {
    this.serviceFee.translations = this.prepareTranslations();
    if (!this.isModelChanged) {
      this.close();
    } else {
      const pendingChanges: PendingChangesDialogComponent = new PendingChangesDialogComponent(this.dialog, this.translateService, {
        pendingChangesDescription: this.translateService.instant('general.api_fees_unsaved_changes'),
        canNotSave: !this.isValid,
        buttonOk: !this.serviceFee.id ? this.translateService.instant('general.api_fees_create') :
          this.translateService.instant('general.api_fees_save')
      });
      pendingChanges.dialogRef.afterClosed().subscribe((res) => {
        if (res === true) {
          this.saveFee();
        } else if (res === CONTINUE_WITHOUT_SAVING) {
          this.close();
        }
      });
    }
  }
  private close(): void {
    this.dialogRef.disableClose = false;
    this.dialogRef.close();
  }

  public get periodPricesForPostList(): string[] {
    return Object.keys(this.periodPricesForPost) || [];
  }

  protected readonly CommissionLevel = CommissionLevel;
  protected readonly NumericInputType = NumericInputType;
  protected readonly SERVICE_FEE_TRANSLATION_MAX_LENGTH = SERVICE_FEE_TRANSLATION_MAX_LENGTH;
  protected readonly SERVICE_FEE_NAME_MAX_LENGTH = SERVICE_FEE_NAME_MAX_LENGTH;
}
